import Icon, { SettingOutlined } from "@ant-design/icons";
import BrushSizeIcon from "@images/icons/icon-brush-size.svg?react";
import CircleIcon from "@images/icons/icon-circle.svg?react";
import { Col, Divider, Dropdown, type MenuProps, Popover, Row } from "antd";
import FastTooltip from "components/fast_tooltip";
import { useWkSelector } from "libs/react_hooks";
import { type ReactElement, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { userSettings } from "types/schemas/user_settings.schema";
import { Unicode } from "viewer/constants";
import defaultState from "viewer/default_state";
import { getViewportExtents } from "viewer/model/accessors/view_mode_accessor";
import { getMaximumBrushSize } from "viewer/model/accessors/volumetracing_accessor";
import { updateUserSettingAction } from "viewer/model/actions/settings_actions";
import { setMousePositionAction } from "viewer/model/actions/volumetracing_actions";
import Store, { type BrushPresets } from "viewer/store";
import ButtonComponent from "viewer/view/components/button_component";
import { LogSliderSetting } from "viewer/view/left_border_tabs/components/log_slider_setting";

function BrushPresetButton({
  name,
  icon,
  brushSize,
  onClick,
}: {
  name: string;
  onClick: () => void;
  icon: ReactElement;
  brushSize: number;
}) {
  const { ThinSpace } = Unicode;
  return (
    <>
      <div style={{ textAlign: "center" }}>
        <ButtonComponent onClick={onClick} icon={icon} />
      </div>
      <div style={{ textAlign: "center" }}>{name}</div>
      <div style={{ lineHeight: "50%", opacity: 0.6, textAlign: "center", fontSize: 12 }}>
        {brushSize}
        {ThinSpace}vx
      </div>
    </>
  );
}

export function getDefaultBrushSizes(maximumSize: number, minimumSize: number) {
  return {
    small: Math.max(minimumSize, 10),
    medium: calculateMediumBrushSize(maximumSize),
    large: maximumSize,
  };
}

export function ChangeBrushSizePopover() {
  const dispatch = useDispatch();
  const brushSize = useWkSelector((state) => state.userConfiguration.brushSize);
  const [isBrushSizePopoverOpen, setIsBrushSizePopoverOpen] = useState(false);
  const maximumBrushSize = useWkSelector((state) => getMaximumBrushSize(state));

  const defaultBrushSizes = getDefaultBrushSizes(maximumBrushSize, userSettings.brushSize.minimum);
  const presetBrushSizes = useWkSelector((state) => state.userConfiguration.presetBrushSizes);

  const handleUpdateBrushSize = useCallback(
    (value: number) => {
      dispatch(updateUserSettingAction("brushSize", value));
    },
    [dispatch],
  );

  const handleUpdatePresetBrushSizes = useCallback(
    (brushSizes: BrushPresets) => {
      dispatch(updateUserSettingAction("presetBrushSizes", brushSizes));
    },
    [dispatch],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Needs investigation whether defaultBrushSizes is needed as dependency.
  useEffect(() => {
    if (presetBrushSizes == null) {
      handleUpdatePresetBrushSizes(defaultBrushSizes);
    }
  }, [presetBrushSizes]);

  let smallBrushSize: number, mediumBrushSize: number, largeBrushSize: number;
  if (presetBrushSizes == null) {
    smallBrushSize = defaultBrushSizes.small;
    mediumBrushSize = defaultBrushSizes.medium;
    largeBrushSize = defaultBrushSizes.large;
  } else {
    smallBrushSize = presetBrushSizes?.small;
    mediumBrushSize = presetBrushSizes?.medium;
    largeBrushSize = presetBrushSizes?.large;
  }

  const centerBrushInViewport = () => {
    const position = getViewportExtents(Store.getState());
    const activeViewPort = Store.getState().viewModeData.plane.activeViewport;
    dispatch(
      setMousePositionAction([position[activeViewPort][0] / 2, position[activeViewPort][1] / 2]),
    );
  };

  const items: MenuProps["items"] = [
    {
      label: "Assign current brush size to",
      key: "assignToParent",
      children: [
        {
          label: (
            <div
              onClick={() =>
                handleUpdatePresetBrushSizes({
                  small: brushSize,
                  medium: mediumBrushSize,
                  large: largeBrushSize,
                })
              }
            >
              Small brush
            </div>
          ),
          key: "assignToSmall",
        },
        {
          label: (
            <div
              onClick={() =>
                handleUpdatePresetBrushSizes({
                  small: smallBrushSize,
                  medium: brushSize,
                  large: maximumBrushSize,
                })
              }
            >
              Medium brush
            </div>
          ),
          key: "assignToMedium",
        },
        {
          label: (
            <div
              onClick={() =>
                handleUpdatePresetBrushSizes({
                  small: smallBrushSize,
                  medium: mediumBrushSize,
                  large: brushSize,
                })
              }
            >
              Large brush
            </div>
          ),
          key: "assignToLarge",
        },
      ],
    },
    {
      label: <div onClick={() => handleUpdatePresetBrushSizes(defaultBrushSizes)}>Reset</div>,
      key: "reset",
    },
  ];

  return (
    <FastTooltip title="Change the brush size">
      <Popover
        title="Brush Size"
        content={
          <div
            style={{
              width: 240,
            }}
            onMouseEnter={() => centerBrushInViewport()}
          >
            <Row align="middle" style={{ textAlign: "center" }}>
              <Col>
                <LogSliderSetting
                  label=""
                  roundToDigit={0}
                  min={userSettings.brushSize.minimum}
                  max={maximumBrushSize}
                  precision={0}
                  spans={[0, 18, 6]}
                  value={brushSize}
                  onChange={handleUpdateBrushSize}
                  defaultValue={defaultState.userConfiguration.brushSize}
                />
              </Col>
              <Col>
                <Dropdown
                  menu={{ items }}
                  trigger={["click", "contextMenu", "hover"]}
                  placement="bottomLeft"
                >
                  <ButtonComponent aria-label="Settings" icon={<SettingOutlined />} />
                </Dropdown>
              </Col>
            </Row>
            <Divider style={{ marginBottom: 15, marginTop: 15 }} />
            <Row justify="space-between" align="middle">
              <Col>
                <BrushPresetButton
                  name="Small"
                  onClick={() => handleUpdateBrushSize(smallBrushSize)}
                  icon={<Icon component={CircleIcon} style={{ transform: "scale(0.6)" }} />}
                  brushSize={Math.round(smallBrushSize)}
                />
              </Col>
              <Col>
                <BrushPresetButton
                  name="Medium"
                  onClick={() => handleUpdateBrushSize(mediumBrushSize)}
                  icon={<Icon component={CircleIcon} style={{ transform: "scale(0.8)" }} />}
                  brushSize={Math.round(mediumBrushSize)}
                />
              </Col>
              <Col>
                <BrushPresetButton
                  name="Large"
                  onClick={() => handleUpdateBrushSize(largeBrushSize)}
                  icon={<Icon component={CircleIcon} style={{ transform: "scale(1.2)" }} />}
                  brushSize={Math.round(largeBrushSize)}
                />
              </Col>
            </Row>
          </div>
        }
        trigger="click"
        open={isBrushSizePopoverOpen}
        placement="bottom"
        style={{
          cursor: "pointer",
        }}
        onOpenChange={(open: boolean) => {
          setIsBrushSizePopoverOpen(open);
          if (open) centerBrushInViewport();
          else dispatch(setMousePositionAction(null));
        }}
      >
        <ButtonComponent
          aria-label="Brush Size"
          style={{
            width: 36,
            padding: 0,
          }}
          icon={<Icon component={BrushSizeIcon} />}
        />
      </Popover>
    </FastTooltip>
  );
}

function calculateMediumBrushSize(maximumBrushSize: number) {
  return Math.ceil((maximumBrushSize - userSettings.brushSize.minimum) / 10) * 5;
}
